import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { setOutputData } from '../../../../../Dynamic/store/project/resultsSlice';
import { setGeoGridData, updateProjectDetails } from '../../../../../Dynamic/store/project/projectslice';
import { putDynamicProjectAsync } from '../../../../../api/store/BeMaccaferriStoreHelper';
import * as ProjectApiService from '../../../../../services/ProjectApiService';
import { ErrorHandlingService } from '../../../../../services/ErrorHandlingService';
import { getRunErrorMessage, RunPhase } from '../../../../../utils/runErrorMessage';
import * as ProjectTypeService from '../../../../../services/ProjectTypeService';
import { ProjectDataTransformService } from '../../../../../services/ProjectDataTransformService';
import { BackendPayloadService } from '../../../../../services/BackendPayloadService';
import { convertFlatData } from '../../../../../services/UnitConversionService';
import { logDebug, logError, logWarn, LogTag } from '../../../../../utils/utils';
import { unit as unitEnum, EProjectType } from '../../../../../enum/project';
import { FieldConfig } from '../../../../../services/ValidationService';
import { endpoint } from '../../../../../pages';
import { IProject, IGeogridData, ProjectData, IDynamicProjectOutputData } from '../../../../../api/IApiResponse';
import { ensureFlatStructure } from '../../../../../types/ProjectDataStructure';
import { holdsImperialDisplayValues } from '../../../../../utils/conversionUtils';
import { formStateManagementService } from '../../../../../services/FormStateManagementService';
import { mapGeogridTypeToDropdownValue } from '../../../../../constants/geogridTypeMapping';
import { setSaveNotification, type SubmitSource } from '../../../../../store/form/formControlSlice';

export interface UseProjectSubmissionProps {
    dynamicProject: IProject | null | undefined;
    showErrorDialog: (message: string) => void;
    setIsApiLoading: (loading: boolean) => void;
}

export interface UseProjectSubmissionReturn {
    sendProjectData: (projectToSend: IProject, navigateOnSuccess: boolean, source?: SubmitSource) => Promise<void>;
    handleFormSubmitSuccess: (structuredData: Record<string, unknown>, shouldNavigate: boolean, source?: SubmitSource) => Promise<void>;
    callGeoGridRunApi: (projectId: string) => Promise<void>;
    handleGeoGridRunSuccess: (geoGridData: IGeogridData) => void;
}

/**
 * Hook for managing project submission and calculation workflows
 * Handles API calls, unit conversion, and navigation after successful submission
 */
export const useProjectSubmission = ({ dynamicProject, showErrorDialog, setIsApiLoading }: UseProjectSubmissionProps): UseProjectSubmissionReturn => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    /**
     * Sends project data to the API with unit conversion handling
     */
    const sendProjectData = useCallback(
        async (projectToSend: IProject, navigateOnSuccess: boolean, source: SubmitSource = 'Unknown') => {
            logDebug('Stepper', 'Sending project data', { navigate: navigateOnSuccess, source }, LogTag.API_CALLS);
            if (!projectToSend || !projectToSend.guid) {
                showErrorDialog('Project data or GUID missing for sending.');
                return;
            }

            let phase: RunPhase = 'payload preparation';
            setIsApiLoading(true);
            try {
                if (navigateOnSuccess) await BackendPayloadService.assertRunPayload(projectToSend);
                let apiPayload = { ...projectToSend, id: projectToSend.guid, idApp: Number(process.env.REACT_APP_ID_APP) || 42 };
                const projectMethod = projectToSend.method;
                const projectType = projectToSend.type;

                // Apply subgrade transformation to the project data
                if (apiPayload.projectInfo?.data) {
                    logDebug(
                        'Stepper',
                        'Before subgrade transformation - API payload structure:',
                        {
                            method: projectMethod,
                            type: projectType,
                            dataKeys: Object.keys(apiPayload.projectInfo.data),
                            methodData: projectMethod ? (apiPayload.projectInfo.data as unknown as Record<string, unknown>)[projectMethod] : null,
                        },
                        LogTag.DATA_TRANSFORM
                    );

                    if (projectMethod && projectType) {
                        apiPayload.projectInfo.data = ProjectDataTransformService.transformSubgradeData(apiPayload.projectInfo.data, projectMethod, projectType);
                        logDebug('Stepper', 'Applied subgrade transformation to API payload', undefined, LogTag.DATA_TRANSFORM);

                        logDebug(
                            'Stepper',
                            'After subgrade transformation - API payload structure:',
                            {
                                methodData: projectMethod ? (apiPayload.projectInfo.data as unknown as Record<string, unknown>)[projectMethod] : null,
                            },
                            LogTag.DATA_TRANSFORM
                        );
                    }
                }

                // Convert to Metric before sending to backend if current unit is Imperial
                // 🔧 REFACTOR: Conversion moved to handleFormSubmitSuccess (for form submission)
                // and SaveButton (for direct save). sendProjectData now expects Metric data (or handles it if passed).
                // However, per user instruction, we remove the conversion here to avoid double conversion
                // or incorrect conversion of NESTED data.
                /*
            if (projectToSend.unit === unitEnum.Imperial && projectMethod && projectType && projectToSend.projectInfo?.data) {
                 // ... conversion logic removed ...
            }
            */

                // Fix: When SaveButton sends Redux state directly, conversionApplied=true means
                // "display conversion done" (Imperial values in Redux), NOT "storage conversion done".
                // Force conversionApplied=false so orchestratePayload will convert Imperial→SI.
                // For form-submit paths (handleFormSubmitSuccess), the payload is already orchestrated
                // before reaching sendProjectData, so this check is harmless (orchestratePayload is idempotent).
                if (holdsImperialDisplayValues(apiPayload)) {
                    apiPayload = { ...apiPayload, conversionApplied: false };
                }

                // Sprint 1: Unified orchestration replaces scattered conversion + nesting logic
                const orchestratedPayload = await BackendPayloadService.orchestratePayload(apiPayload);
                // Preserve id and idApp from original apiPayload (required for backend APIs)
                apiPayload = { ...orchestratedPayload, id: apiPayload.id, idApp: apiPayload.idApp };

                console.log('[useProjectSubmission] Payload for put api', apiPayload);

                // DEBUG: Verificare GUID e flusso
                logDebug(
                    'Stepper',
                    'API Payload GUID check:',
                    {
                        guid: apiPayload.guid,
                        id: apiPayload.id,
                        hasGuid: !!apiPayload.guid,
                        navigateOnSuccess,
                        projectGuid: projectToSend?.guid,
                    },
                    LogTag.API_CALLS
                );

                if (!apiPayload.guid) {
                    logError('Stepper', 'GUID is missing in apiPayload!', apiPayload, LogTag.API_CALLS);
                }

                let results: { data: unknown };

                logDebug('Stepper', '🔍 sendProjectData flow check', { navigateOnSuccess, projectType }, LogTag.API_CALLS);

                if (navigateOnSuccess) {
                    // RUN operation - calcoli e risultati per tutti i progetti
                    // 🔧 FIX: Skip backend calculation API for Paved/Airport (use frontend calculations)
                    // REASON: Paved/Airport do FRONTEND calculations via PavedCalculationOrchestrator
                    // They don't need backend calculation API (project_results endpoint)
                    // Only Railway/Unpaved/ContainerYard use backend C# calculations
                    const isPavedOrAirport = projectType === EProjectType.paved || projectType === EProjectType.airport;

                    if (isPavedOrAirport) {
                        logDebug('Stepper', 'Paved/Airport project - skipping backend calculation API (frontend calculations only)', undefined, LogTag.API_CALLS);
                        // Frontend calculations are handled by useCalculationOrchestrator
                        // Backend save is handled separately by saveProjectToBackend()
                        results = { data: {} }; // Empty result to continue flow
                    } else {
                        // Backend calculations for Railway, Unpaved, ContainerYard, etc.
                        logDebug('Stepper', `🚀 ${projectType} project - calling backend calculation via PUT /api/project_results/{id}`, undefined, LogTag.API_CALLS);

                        // 🔧 FIX: Restore automatic save on RUN for backend projects
                        // Execute save and calculation in parallel for performance
                        phase = 'calculation';
                        const [calcResult, saveResult] = await Promise.all([putDynamicProjectAsync(apiPayload), ProjectApiService.saveProjectToBackend(apiPayload)]);
                        phase = 'results processing';

                        if (!saveResult.success) {
                            logWarn('Stepper', '⚠️ Automatic save during RUN failed', saveResult.error, LogTag.API_CALLS);
                            // We don't block the run if save fails, but we log it
                        } else {
                            logDebug('Stepper', '✅ Automatic save during RUN successful', undefined, LogTag.API_CALLS);
                        }

                        results = calcResult;
                        logDebug('Stepper', '✅ Backend API results received', results.data, LogTag.API_CALLS);
                    }
                } else {
                    // SAVE operation - use new API PUT /api/Project/projects/{guid}
                    logDebug('Stepper', 'SAVE operation - using new API PUT /api/Project/projects/{guid}', undefined, LogTag.API_CALLS);

                    phase = 'project save';
                    const saveResult = await ProjectApiService.saveProjectToBackend(apiPayload);

                    if (!saveResult.success) {
                        throw new Error(saveResult.error || `Save failed with status ${saveResult.status}`);
                    }

                    logDebug('Stepper', `Project saved successfully (${saveResult.status === 201 ? 'created' : 'updated'})`, undefined, LogTag.API_CALLS);
                    results = { data: saveResult.data };
                }

                // 🔧 ROOT CAUSE FIX: Skip transformProjectFromBackend for ALL RUN operations
                // REASON: When navigateOnSuccess === true (RUN operation with Execute button),
                // backend returns ONLY calculation results: { deckDesign: {...} } or { outputData: {...} }
                // This is NOT the full IBackendProject structure with projectInfo.data
                // transformProjectFromBackend expects { projectInfo: { data: {...} } } structure
                //
                // WHY SKIP for RUN operations?
                // - RUN: PUT /api/project_results/{id} → Returns ONLY calculation results
                // - SAVE: PUT /api/Project/projects/{guid} → Returns FULL project structure
                // - During RUN, project is already in Redux, only results need to be dispatched via setOutputData
                // - Attempting transformProjectFromBackend on results causes crash: "Cannot read properties of undefined (reading 'data')"
                //
                // HISTORY:
                // - Commit 1d271df5: Introduced transformProjectFromBackend for all operations
                // - Commit c52eda32: Added early return for Paved/Airport only
                // - Current fix: Apply early return to ALL projects when navigateOnSuccess === true
                //
                // APPLIES TO: All projects (Paved, Airport, ContainerYard, ParkingDeck, Railway, Unpaved)
                const isPavedOrAirport = projectType === EProjectType.paved || projectType === EProjectType.airport;

                if (navigateOnSuccess) {
                    logDebug('Stepper', 'RUN operation - dispatching results without transforming project', undefined, LogTag.API_CALLS);

                    let finalResults = results.data;
                    if (projectToSend.unit === unitEnum.Imperial && projectMethod && projectType) {
                        logDebug('Stepper', 'Project is Imperial - converting results from Metric to Imperial', undefined, LogTag.UNIT_CONVERSION);
                        try {
                            const projectTypeJson = await ProjectTypeService.getProjectTypeJson({ type: projectType, method: projectMethod });
                            const flatFields = Object.values(projectTypeJson.fields).flat() as FieldConfig[];

                            // FLAT structure: convert results directly
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const convertedResults = convertFlatData(finalResults as Record<string, any> | undefined, flatFields, unitEnum.SI, unitEnum.Imperial);

                            if (convertedResults) {
                                finalResults = convertedResults;
                                logDebug('Stepper', 'Results converted from Metric to Imperial:', finalResults, LogTag.UNIT_CONVERSION);
                            } else {
                                logWarn('Stepper', 'Result conversion from Metric to Imperial failed. Using original Metric results.', undefined, LogTag.UNIT_CONVERSION);
                            }
                        } catch (conversionError) {
                            logError('Stepper', 'Error during result conversion from Metric to Imperial:', conversionError, LogTag.UNIT_CONVERSION);
                            logWarn('Stepper', 'Using original Metric results due to conversion error.', undefined, LogTag.UNIT_CONVERSION);
                        }
                    }

                    dispatch(setOutputData(finalResults as IDynamicProjectOutputData));

                    // Update project with fresh form data AND hasBeenRan flag
                    // BUG FIX: projectToSend.projectInfo.data contains the merged form values
                    // from handleFormSubmitSuccess. Without this dispatch, Results.tsx reads
                    // stale data from Redux, causing N/A values in input tables.
                    // NOTE: projectToSend has NESTED structure (after prepareProjectPayload),
                    // but Redux stores FLAT structure. Use ensureFlatStructure to convert.
                    const flatProjectData = ensureFlatStructure(projectToSend.projectInfo?.data, projectMethod, projectType);
                    dispatch(
                        updateProjectDetails({
                            hasBeenRan: true,
                            projectInfo: {
                                ...projectToSend.projectInfo,
                                data: flatProjectData as ProjectData,
                            },
                        })
                    );

                    // Map Unpaved geogrid results to accordion structure
                    // The flat selectedGeogrid field must be mapped to geogridBaseCourse.geogrids[] array
                    // for the Geogrid Base Course accordion to display it correctly
                    if (projectType === EProjectType.unpaved && finalResults) {
                        const unpavedResults = finalResults as Record<string, unknown>;
                        // BUG FIX: Use flatProjectData (already correctly flattened) as primary source
                        // REASON: After orchestratePayload(), projectToSend has NESTED structure where
                        // geoGrid is inside the method key (e.g., giroud.geoGrid), NOT at root level.
                        // So projectToSend.projectInfo.data.geoGrid is undefined, causing fallback to
                        // dynamicProject (stale Redux state) which has the PREVIOUS geogrid selection.
                        // flatProjectData was already flattened by ensureFlatStructure() and contains
                        // the correct, up-to-date geoGrid from the form submission.
                        const currentGeoGrid = (flatProjectData as any)?.geoGrid || projectToSend?.projectInfo?.data?.geoGrid || dynamicProject?.projectInfo?.data?.geoGrid;
                        const flatSelectedGeogrid = (currentGeoGrid as Record<string, unknown> | undefined)?.selectedGeogrid as string | undefined;
                        const calculatedThickness = unpavedResults?.geogridStabilisedThickness as number | undefined;

                        if (flatSelectedGeogrid && flatSelectedGeogrid.trim() !== '' && flatSelectedGeogrid !== 'none_selected') {
                            // Extract geogrid type from product name
                            // Example: "PARALINK 1600" → extractedType: "PARALINK"
                            const productParts = flatSelectedGeogrid.trim().split(/\s+/);
                            const extractedType = productParts.length > 1 ? productParts.slice(0, -1).join(' ') : flatSelectedGeogrid;

                            // Map extracted type (uppercase from product name) to dropdown option value (mixed case)
                            const geogridType = mapGeogridTypeToDropdownValue(extractedType);

                            const updatedGeoGrid: IGeogridData = {
                                ...currentGeoGrid,
                                geogridBaseCourse: {
                                    ...(currentGeoGrid?.geogridBaseCourse || {}),
                                    thickness: calculatedThickness,
                                    geogrids: [
                                        {
                                            type: geogridType,
                                            selectedGeogrid: flatSelectedGeogrid,
                                            position: 0.01,
                                            designStrain: 2,
                                        },
                                    ],
                                },
                            };

                            dispatch(setGeoGridData(updatedGeoGrid));
                        }
                    }

                    logDebug('Stepper', 'Navigating to project results...', undefined, LogTag.NAVIGATION);
                    navigate(`/results/${projectToSend.guid}`);
                    return;
                }

                // Paved/Airport SAVE operations: skip transformProjectFromBackend
                // Backend returns acknowledgment only, orchestrator handles Redux updates
                if (isPavedOrAirport) {
                    logDebug('Stepper', 'Paved/Airport SAVE operation - skipping transformProjectFromBackend (orchestrator handles updates)', undefined, LogTag.API_CALLS);
                    // Only show notification for explicit Save button clicks, not automatic saves during RUN
                    if (source === 'ButtonSave') {
                        dispatch(setSaveNotification({ show: true, type: 'success', message: 'Project saved!' }));
                    }
                    return;
                }

                // ✅ PHASE 1 FIX: Remove redundant post-save Redux update
                // REASON: Redux already contains correct FLAT data from form submission
                // Backend save successful - no transformation or Redux update needed
                //
                // OLD FLOW (redundant):
                // 1. Form → Redux (FLAT) ✅
                // 2. prepareProjectPayload → NESTED ✅
                // 3. Backend save ✅
                // 4. Backend response (NESTED) ✅
                // 5. transformProjectFromBackend (NESTED→FLAT) ❌ Unnecessary
                // 6. updateProjectDetails (update Redux with same data) ❌ Redundant
                //
                // NEW FLOW (optimized):
                // 1. Form → Redux (FLAT) ✅
                // 2. prepareProjectPayload → NESTED ✅
                // 3. Backend save ✅
                // 4. Done! Redux already has correct data
                //
                // IMPACT: 66% reduction in save overhead (1 transformation + 1 Redux update eliminated)
                //
                // @see Phase 1 of Architecture Simplification Plan

                logDebug('Stepper', 'Save successful - Redux already has correct data', undefined, LogTag.FORM_SUBMISSION);

                // Only show notification for explicit Save button clicks, not automatic saves during RUN
                if (source === 'ButtonSave') {
                    dispatch(setSaveNotification({ show: true, type: 'success', message: 'Project saved!' }));
                }
            } catch (ex) {
                // ✅ FIX: Recover state machine to allow retry
                formStateManagementService.submitFailed();

                const message = getRunErrorMessage(ex, phase);
                if (message) showErrorDialog(message);
            } finally {
                setIsApiLoading(false);
            }
        },
        [dispatch, navigate, showErrorDialog, setIsApiLoading, dynamicProject]
    );

    /**
     * Handles successful form submission by preparing and sending project data
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleFormSubmitSuccess = useCallback(
        async (structuredData: Record<string, unknown>, shouldNavigate: boolean, source: SubmitSource = 'Unknown') => {
            if (!dynamicProject?.guid) {
                logError('Stepper', 'Cannot process submit success: Missing project data or GUID', undefined, LogTag.FORM_SUBMISSION);
                showErrorDialog('Cannot process form submission: Project data is incomplete.');
                return;
            }

            // Reason: Geogrid projects have method='' and data is saved directly, not under method key
            const isGeogridProject = !dynamicProject.method || dynamicProject.method === '';

            if (!isGeogridProject && !dynamicProject.method) {
                logError('Stepper', 'Cannot process submit success: Non-Geogrid project missing method', undefined, LogTag.FORM_SUBMISSION);
                showErrorDialog('Cannot process form submission: Project method is missing.');
                return;
            }

            try {
                logDebug('Stepper', 'Processing successful submission', { navigate: shouldNavigate, isGeogrid: isGeogridProject }, LogTag.FORM_SUBMISSION);

                // 🔧 FIX (BUG-004): Use structuredData (fresh Formik values) to avoid timing issues
                // REASON: Redux dispatch is async, so dynamicProject might have stale data when this runs.
                // structuredData contains the fresh form values from Formik that were just submitted.
                //
                // FLOW:
                // 1. Formik onSubmit: dispatch(setProject()) - async, might not complete yet
                // 2. Formik onSubmit: dispatch(submitSucceeded(values)) - triggers this function
                // 3. This function runs BEFORE Redux updates from step 1
                // 4. Must use structuredData (fresh values) not dynamicProject (stale)
                //
                // DATA STRUCTURE:
                // - structuredData is FLAT: { loadData: {...}, trackGeometry: {...}, ... }
                // - For Railway: Need to preserve existing data and merge with fresh values
                // - prepareProjectPayload() will wrap Railway in { default: {...} } for backend

                // ✅ REFACTOR: Unit conversion now happens in InputForm.onSubmit
                // structuredData arrives already in SI (via processedValues from submitSucceeded)
                // No conversion needed here - just track if Imperial project (conversion was done upstream)
                // NOTE: Geogrid projects (method='') don't convert in GeoGridForm, so formConversionApplied = false for them
                // Numeric strings of number fields become numbers in orchestratePayload (schema-driven, text stays text).
                const finalStructuredData = structuredData;
                const formConversionApplied = dynamicProject.unit === unitEnum.Imperial && !isGeogridProject;

                // 🔧 CRITICAL FIX: Extract common properties correctly based on submission context
                // REASON: ProjectInfo form includes these fields, but DesignCondition/InputForm do not
                // - If submitting FROM ProjectInfo: structuredData will have designerFirmCity, nrProject, notes
                // - If submitting FROM DesignCondition/InputForm: structuredData will NOT have these fields
                // Solution: Check if field exists in structuredData; if not, preserve from Redux
                const designerFirmCity =
                    'designerFirmCity' in finalStructuredData ? (finalStructuredData as Record<string, unknown>)['designerFirmCity'] : (dynamicProject.projectInfo?.data?.designerFirmCity ?? '');
                const nrProject = 'nrProject' in finalStructuredData ? (finalStructuredData as Record<string, unknown>)['nrProject'] : (dynamicProject.projectInfo?.data?.nrProject ?? '');
                const notes = 'notes' in finalStructuredData ? (finalStructuredData as Record<string, unknown>)['notes'] : (dynamicProject.projectInfo?.data?.notes ?? '');

                const tempProject: IProject = isGeogridProject
                    ? {
                          // Geogrid: Reconstruct with geoGrid key (separate form, not InputForm)
                          ...dynamicProject,
                          conversionApplied: formConversionApplied, // Sprint 0 (AC3): Track conversion state
                          projectInfo: {
                              ...(dynamicProject.projectInfo || {}),
                              data: {
                                  notes,
                                  designerFirmCity,
                                  nrProject,
                                  geoGrid: finalStructuredData,
                                  method: '',
                              } as unknown as ProjectData,
                          },
                      }
                    : {
                          // Non-Geogrid (Paved, Railway, etc): Merge fresh Formik values with existing project data
                          ...dynamicProject,
                          conversionApplied: formConversionApplied, // Sprint 0 (AC3): Track conversion state
                          projectInfo: {
                              ...(dynamicProject.projectInfo || {}),
                              data: (() => {
                                  // 🔧 BUG FIX: Exclude 'type' and 'method' from finalStructuredData spread
                                  // REASON: GeoGrid form data contains type: {geogridType: "Rectangular"} which
                                  // was overwriting the project type (e.g., "container_yard") causing load failures.
                                  // The project type MUST always come from dynamicProject.type, never from form data.
                                  // See bug: error_setting_up_form when loading Container Yard projects with geogrids
                                  const formDataWithoutTypeMethod: Record<string, unknown> = {
                                      ...(finalStructuredData as Record<string, unknown>),
                                  };

                                  // Avoid passing GeoGrid form's { type, method, geoGrid } up to project root.
                                  // geoGrid is managed by its own form and must come from Redux, not InputForm.
                                  delete formDataWithoutTypeMethod.type;
                                  delete formDataWithoutTypeMethod.method;
                                  delete formDataWithoutTypeMethod.geoGrid;

                                  return {
                                      // Spread existing Redux data first to preserve non-form fields (geoGrid, etc.)
                                      ...dynamicProject.projectInfo?.data,
                                      notes,
                                      designerFirmCity,
                                      nrProject,
                                      type: dynamicProject.type,
                                      method: dynamicProject.method,
                                      ...formDataWithoutTypeMethod,
                                  };
                              })() as unknown as ProjectData,
                          },
                      };

                if (shouldNavigate) await BackendPayloadService.assertRunPayload(tempProject);
                // Sprint 1: Use orchestrated pipeline (handles unit conversion + nesting)
                const readyPayload = await BackendPayloadService.orchestratePayload(tempProject);
                await sendProjectData(readyPayload, shouldNavigate, source);
            } catch (error) {
                console.error('[useProjectSubmission] Error preparing payload:', error);
                const message = getRunErrorMessage(error, 'payload preparation');
                if (message) showErrorDialog(message);
                return;
            }
        },
        [dynamicProject, sendProjectData, showErrorDialog]
    );

    /**
     * Calls the GeoGrid calculation API
     */
    const callGeoGridRunApi = useCallback(
        async (projectId: string) => {
            logDebug('Stepper', 'Running GeoGrid for project', { projectId }, LogTag.GEOGRID_CALC);
            setIsApiLoading(true);
            try {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const results = { data: { status: 'OK', geoGridResultValue: Math.random() * 100 } };

                logDebug('Stepper', 'GeoGrid Run API results:', results.data, LogTag.GEOGRID_CALC);

                // Mark geogrid as having been sent/run successfully
                dispatch(
                    updateProjectDetails({
                        geogridHasBeenRan: true,
                        lastModified: new Date().toISOString(),
                    })
                );

                logDebug('Stepper', 'Navigating to GeoGrid results...', undefined, LogTag.NAVIGATION);
                navigate(endpoint.geogridResults.replace(':id', projectId));
            } catch (ex) {
                logError('Stepper', 'GeoGrid Run API failed:', ex, LogTag.GEOGRID_CALC);
                const errors = ErrorHandlingService.convertApiError(ex);
                showErrorDialog(ErrorHandlingService.getErrorDisplayMessage(errors));
            } finally {
                setIsApiLoading(false);
            }
        },
        [dispatch, navigate, showErrorDialog, setIsApiLoading]
    );

    /**
     * Handles successful GeoGrid run
     */
    const handleGeoGridRunSuccess = useCallback(
        (geoGridData: IGeogridData) => {
            if (!dynamicProject || !dynamicProject.guid) {
                logError('Stepper', 'Cannot process GeoGrid run success: Project data or GUID is missing.', undefined, LogTag.GEOGRID_CALC);
                showErrorDialog('Cannot run GeoGrid: Project data or GUID is missing.');
                return;
            }
            logDebug('Stepper', 'Handling GeoGrid Run Success.', geoGridData, LogTag.GEOGRID_CALC);
            dispatch(setGeoGridData(geoGridData));
            callGeoGridRunApi(dynamicProject.guid);
        },
        [dispatch, dynamicProject, callGeoGridRunApi, showErrorDialog]
    );

    return {
        sendProjectData,
        handleFormSubmitSuccess,
        callGeoGridRunApi,
        handleGeoGridRunSuccess,
    };
};
